// apps/client/src/ui/screens/blocked.tsx (M2-T08, spec §10.1) — the players you blocked, newest first, each with Unblock. Opened
// from Settings. A deleted account's block shows as "a former resident".
import type { UserId } from '@whiskerton/shared';
import { useEffect, useState } from 'react';
import { HttpError } from '../../api/http';
import { safetyBlockedNow, safetyReady } from '../../state/slices/safety';
import { useGame } from '../../state/store';
import { useT } from '../i18n';
import { Panel } from '../kit';
import type { ScreenDef } from '../router';
import { SafetyButton } from './report';

declare module '../router' { interface ScreenParams { blocked: undefined } }

const failed = (e: unknown) => useGame.getState().toast(e instanceof HttpError ? (`errors.${e.error}` as const) : 'errors.server_error', undefined, 'warn');

function Blocked({ close }: { params: undefined; close: () => void }) {
  const t = useT();
  const blocked = useGame(safetyBlockedNow);
  const ready = useGame(safetyReady);
  const [busy, setBusy] = useState<string | null>(null);
  useEffect(() => {
    void useGame.getState().safetyLoad().catch(failed);
  }, []);
  const rows = Object.entries(blocked).sort(([, a], [, b]) => b.at - a.at);
  const unblock = async (userId: string) => {
    setBusy(userId);
    try { await useGame.getState().safetyUnblock(userId as UserId); }
    catch (e) { failed(e); }
    finally { setBusy(null); }
  };
  return (
    <Panel title={t('safety.blockedTitle')}>
      <div style={{ display: 'grid', gap: 12, width: 'min(440px, 72cqw)', maxWidth: '100%' }}>
        {!ready && rows.length === 0 ? <p style={{ margin: 0 }}>{t('common.loading')}</p> : rows.length === 0 ? <p style={{ margin: 0 }}>{t('safety.blockedEmpty')}</p> : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
            {rows.map(([userId, b]) => (
              <li key={userId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                padding: '8px 8px 8px 14px', borderRadius: 14, background: 'var(--cream-100)' }}>
                <span style={{ fontWeight: 900 }}>{b.name || t('common.formerResident')}</span>
                <SafetyButton testId="unblock" variant="quiet" disabled={busy === userId} onClick={() => void unblock(userId)}>{t('safety.unblock')}</SafetyButton>
              </li>
            ))}
          </ul>
        )}
        <SafetyButton variant="quiet" onClick={close}>{t('common.close')}</SafetyButton>
      </div>
    </Panel>
  );
}
export const screen: ScreenDef<'blocked'> = { id: 'blocked', layer: 'panel', component: Blocked };
